import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import CreateTrendlineStrategy from './CreateStrategy/CreateTrendlineStrategy';
import TrendlineStrategyImporter from './TrendlineStrategyImporter';
import StrategyList from './StrategyList/StrategyList';
import { transformFromApiFormat } from '../../utils/strategyUtils';
import { trendlineStrategyService } from '../../config/api';

// SVG Icons
const AddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ListIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
  </svg>
);

const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

/**
 * Breadcrumb Component
 */
const Breadcrumb = ({ items }) => (
  <nav className="flex items-center space-x-2 text-sm mb-6">
    {items.map((item, index) => (
      <React.Fragment key={index}>
        {index > 0 && <ChevronRightIcon />}
        <button
          onClick={item.onClick}
          className={`flex items-center gap-1 hover:text-primary transition-colors ${index === items.length - 1 ? 'text-base-content font-medium' : 'text-base-content/60'
            }`}
          disabled={!item.onClick}
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      </React.Fragment>
    ))}
  </nav>
);

/**
 * StrategyRefactored - Modernized strategy management page
 */
const StrategyRefactored = ({ initialTab }) => {
  const [selectedTab, setSelectedTab] = useState(initialTab || 'list');
  const [importedStrategy, setImportedStrategy] = useState(null);
  const [importKey, setImportKey] = useState(0);
  const [showImporter, setShowImporter] = useState(false);
  const [fetchingStrategy, setFetchingStrategy] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const location = useLocation();

  useEffect(() => {
    if (initialTab) {
      setSelectedTab(initialTab);
    }

    // Check for imported strategy from navigation state (e.g. from Public Strategies)
    if (location.state?.importedStrategy) {
      setImportedStrategy(location.state.importedStrategy);
      setImportKey(prev => prev + 1);
      if (location.state.tab) {
        setSelectedTab(location.state.tab);
      }
      // Clear state to prevent reapplying on refresh/re-render if not handled by router
      window.history.replaceState({}, document.title);
    }
  }, [initialTab, location.state]);

  const handleCreateStrategy = () => {
    setSelectedTab('create');
    setShowImporter(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Breadcrumb items based on current tab
  const getBreadcrumbItems = () => {
    const items = [
      {
        label: 'Home',
        icon: <HomeIcon />,
        onClick: () => setSelectedTab('list')
      },
      {
        label: 'Strategies',
        onClick: selectedTab !== 'list' ? () => setSelectedTab('list') : null
      }
    ];

    if (selectedTab === 'create') {
      items.push({ label: 'New Strategy' });
    }

    return items;
  };

  return (
    <div className="h-full bg-base-200 p-4 overflow-auto">
      {/* Strategy List View */}
      {selectedTab === 'list' && (
        <div className="bg-base-100 rounded-lg shadow-sm h-full">
          {/* Header inside card */}
          <div className="px-6 py-4 border-b border-base-300">
            <Breadcrumb items={getBreadcrumbItems()} />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-base-content">My Strategies</h1>
                <p className="text-base-content/60 text-sm mt-1">
                  Manage and monitor your trading strategies
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  className="btn btn-outline btn-sm gap-2"
                  onClick={() => {
                    setSelectedTab('create');
                    setShowImporter(true);
                  }}
                >
                  <UploadIcon />
                  Import
                </button>
                <button
                  className="btn btn-primary btn-sm gap-2"
                  onClick={handleCreateStrategy}
                >
                  <AddIcon />
                  New Strategy
                </button>
              </div>
            </div>
          </div>
          {/* Strategy List Content */}
          <div className="px-6 -mx-px relative">
            {fetchingStrategy && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-base-100/70 rounded-lg">
                <span className="loading loading-spinner loading-lg text-primary"></span>
                <span className="ml-3 text-sm text-base-content/60">Loading strategy...</span>
              </div>
            )}
            <StrategyList
              onSelectStrategy={async (strategy) => {
                // Fetch the full strategy by ID to get all fields (lotSize, dates, etc.)
                // The list endpoint may return a summary without all fields
                setFetchingStrategy(true);
                try {
                  const response = await trendlineStrategyService.getById(strategy.id);
                  setImportedStrategy(transformFromApiFormat(response.data));
                } catch (err) {
                  console.error('Failed to fetch strategy details:', err);
                  // Fallback to the list object
                  setImportedStrategy(transformFromApiFormat(strategy));
                } finally {
                  setFetchingStrategy(false);
                }
                setImportKey(prev => prev + 1);
                setSelectedTab('create');
              }}
            />
          </div>

        </div>
      )}

      {/* Create Strategy View */}
      {selectedTab === 'create' && (
        <div className="bg-base-100 rounded-lg shadow-sm h-full flex flex-col">
          {/* Header inside card */}
          <div className="px-6 py-4 border-b border-base-300">
            <Breadcrumb items={getBreadcrumbItems()} />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-base-content">Create New Strategy</h1>
                <p className="text-base-content/60 text-sm mt-1">
                  Build a custom trendline-based trading strategy
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  className="btn btn-outline btn-sm gap-2"
                  onClick={() => setShowImporter(!showImporter)}
                >
                  <UploadIcon />
                  {showImporter ? 'Hide Import' : 'Import'}
                </button>
                <button
                  className="btn btn-ghost btn-sm gap-2"
                  onClick={() => setSelectedTab('list')}
                >
                  <ListIcon />
                  View All
                </button>
              </div>
            </div>
          </div>

          {/* Create Strategy Content */}
          <div className="flex-1 overflow-auto">
            {/* Import Strategy Section - Collapsible */}
            {showImporter && (
              <div className="px-6 py-4 border-b border-base-300 bg-base-200/50">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-semibold text-base-content">Import Existing Strategy</h2>
                  <button
                    className="btn btn-ghost btn-xs"
                    onClick={() => setShowImporter(false)}
                  >
                    ✕
                  </button>
                </div>
                <TrendlineStrategyImporter
                  onImport={(strategy) => {
                    setImportedStrategy(strategy);
                    setImportKey(prev => prev + 1);
                    setShowImporter(false);
                    setSnackbar({
                      open: true,
                      message: 'Strategy imported successfully! Form has been populated.',
                      severity: 'success'
                    });
                  }}
                />
              </div>
            )}

            {/* Create Strategy Form */}
            <CreateTrendlineStrategy
              key={importKey}
              inline
              initialData={importedStrategy}
              onSuccess={(strategy) => {
                setSnackbar({
                  open: true,
                  message: 'Strategy created successfully!',
                  severity: 'success'
                });
                setImportedStrategy(null);
                setSelectedTab('list');
              }}
              onClose={() => setSelectedTab('list')}
            />
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {snackbar.open && (
        <div className="toast toast-end toast-top z-50">
          <div className={`alert shadow-lg ${snackbar.severity === 'success' ? 'alert-success' :
            snackbar.severity === 'error' ? 'alert-error' :
              'alert-info'
            }`}>
            <div className="flex items-center gap-2">
              <span>{snackbar.message}</span>
              <button
                className="btn btn-ghost btn-xs btn-circle"
                onClick={handleCloseSnackbar}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategyRefactored;
